import {
  FrequencySchema,
  SyncTargetStatusSchema,
  TopicSchema,
  type CefrLevel,
  type Chunk,
  type Frequency,
  type Topic,
  type VocabEntryWithTargets,
  type VocabListQuery,
} from "@engo/shared";

export type WordFilterDraft = Omit<VocabListQuery, "cursor" | "limit">;

const SMART_TOKEN_RE = /(?:^|\s)(type|tag|status|topic|freq):(\S+)/gi;
const SYNC_STATUSES = new Set<string>(SyncTargetStatusSchema.options);
const TOPICS = new Set<string>(TopicSchema.options);
const FREQUENCIES = new Set<string>(FrequencySchema.options);

export function parseSmartQuery(raw: string): WordFilterDraft {
  const draft: WordFilterDraft = {};
  const rest = raw.replace(SMART_TOKEN_RE, (_match, key: string, value: string) => {
    const k = key.toLowerCase();
    const normalized = value.toLowerCase();
    if (k === "type") draft.type = value;
    else if (k === "tag") draft.tag = value;
    else if (k === "status" && SYNC_STATUSES.has(normalized)) {
      draft.sync_status = normalized as VocabListQuery["sync_status"];
    } else if (k === "topic" && TOPICS.has(normalized)) {
      draft.topic = normalized as Topic;
    } else if (k === "freq" && FREQUENCIES.has(normalized)) {
      draft.frequency = normalized as Frequency;
    }
    return " ";
  });
  const q = rest.trim().replace(/\s+/g, " ");
  if (q) draft.q = q;
  return draft;
}

export interface EditDraft {
  word: string;
  type: string;
  meaning_simple_en: string;
  ipa: string;
  core_idea: string;
  vietnamese: string;
  examples: string;
  chunks: Chunk[];
  synonyms: string;
  antonyms: string;
  speaking_casual: string;
  speaking_neutral: string;
  speaking_formal: string;
  memory_hook: string;
  tags: string;
  topics: Topic[];
  frequency: Frequency;
  cefr_level: CefrLevel;
}

export function buildEditDraft(item: VocabEntryWithTargets): EditDraft {
  return {
    word: item.word,
    type: item.data.type,
    meaning_simple_en: item.data.meaning_simple_en,
    ipa: item.data.ipa,
    core_idea: item.data.core_idea,
    vietnamese: item.vietnamese ?? "",
    examples: item.data.examples.join("\n"),
    chunks: item.data.chunks,
    synonyms: item.data.synonyms.join("\n"),
    antonyms: item.data.antonyms.join("\n"),
    speaking_casual: item.data.speaking_usage.casual,
    speaking_neutral: item.data.speaking_usage.neutral,
    speaking_formal: item.data.speaking_usage.formal,
    memory_hook: item.data.memory_hook,
    tags: item.tags.join(", "),
    topics: item.data.topics,
    frequency: item.data.frequency,
    cefr_level: item.data.cefr_level,
  };
}

export function splitLines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export function toDateInput(value?: string): string {
  if (!value) return "";
  return value.slice(0, 10);
}

export function fromDateInputStart(value: string): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

export function fromDateInputEnd(value: string): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T23:59:59.999Z`).toISOString();
}
